import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Instagram, MessageCircle, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { BRAND, whatsappLink, DISCLAIMER } from "../data";
import { subscribeNewsletter } from "../lib/api";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      toast.error("Please enter a valid email.");
      return;
    }
    setLoading(true);
    try {
      await subscribeNewsletter(email);
      toast.success("You're subscribed. Welcome aboard.");
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="relative border-t border-white/10 bg-[#0a0a0a] pt-24" data-testid="footer">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid gap-12 rounded-3xl border border-white/10 bg-white/[0.02] p-8 md:grid-cols-2 md:p-12">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-500">Newsletter</p>
            <h3 className="mt-3 font-display text-3xl font-semibold tracking-tight md:text-4xl">
              Insights, straight to your inbox.
            </h3>
            <p className="mt-3 max-w-md text-slate-400">
              Occasional, practical guidance on account management and growth. No spam — unsubscribe anytime.
            </p>
          </div>
          <form onSubmit={subscribe} className="flex flex-col justify-center gap-3" data-testid="newsletter-form">
            <div className="flex flex-col gap-3 sm:flex-row">
              <input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-full border border-white/10 bg-[#050505] px-5 py-3.5 text-white placeholder-slate-500 outline-none transition-colors focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                data-testid="newsletter-email-input"
              />
              <button
                type="submit"
                disabled={loading}
                className="flex items-center justify-center gap-2 rounded-full bg-blue-600 px-7 py-3.5 font-medium text-white shadow-[0_0_20px_rgba(37,99,235,0.4)] transition-all duration-300 hover:bg-blue-700 disabled:opacity-60"
                data-testid="newsletter-submit-button"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Subscribe <ArrowRight className="h-4 w-4" /></>}
              </button>
            </div>
          </form>
        </div>

        <div className="grid gap-10 py-16 md:grid-cols-4">
          <div className="md:col-span-2">
            <Link to="/" className="font-display text-2xl font-semibold tracking-tight">
              JOBL<span className="text-blue-500">SX</span>
            </Link>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-slate-400">{BRAND.tagline}.</p>
            <div className="mt-6 flex gap-3">
              <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-slate-300 transition-colors hover:bg-white/10 hover:text-white" data-testid="footer-whatsapp">
                <MessageCircle className="h-4 w-4" />
              </a>
              <a href={BRAND.instagramUrl} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-slate-300 transition-colors hover:bg-white/10 hover:text-white" data-testid="footer-instagram">
                <Instagram className="h-4 w-4" />
              </a>
              <a href={`mailto:${BRAND.email}`} aria-label="Email" className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-slate-300 transition-colors hover:bg-white/10 hover:text-white" data-testid="footer-email">
                <Mail className="h-4 w-4" />
              </a>
            </div>
          </div>

          <div>
            <p className="text-sm font-semibold text-white">Company</p>
            <ul className="mt-4 space-y-3 text-sm text-slate-400">
              <li><Link to="/about" className="transition-colors hover:text-white">About</Link></li>
              <li><Link to="/services" className="transition-colors hover:text-white">Services</Link></li>
              <li><Link to="/testimonials" className="transition-colors hover:text-white" data-testid="footer-reviews-link">Client Reviews</Link></li>
              <li><Link to="/faq" className="transition-colors hover:text-white">FAQ</Link></li>
              <li><Link to="/contact" className="transition-colors hover:text-white">Contact</Link></li>
            </ul>
          </div>
          <div>
            <p className="text-sm font-semibold text-white">Legal</p>
            <ul className="mt-4 space-y-3 text-sm text-slate-400">
              <li><Link to="/privacy" className="transition-colors hover:text-white" data-testid="footer-privacy-link">Privacy Policy</Link></li>
              <li><Link to="/terms" className="transition-colors hover:text-white" data-testid="footer-terms-link">Terms of Service</Link></li>
              <li><Link to="/disclaimer" className="transition-colors hover:text-white" data-testid="footer-disclaimer-link">Disclaimer</Link></li>
            </ul>
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Important Disclaimer</p>
          <p className="mt-2 text-xs leading-relaxed text-slate-500">{DISCLAIMER}</p>
        </div>

        <div className="flex flex-col items-center justify-between gap-4 border-t border-white/10 py-8 text-sm text-slate-500 md:flex-row">
          <p>© {new Date().getFullYear()} JOBLSX. All rights reserved.</p>
          <p>Digital Consulting & Account Management</p>
        </div>
      </div>
    </footer>
  );
}
